"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, Calendar, TrendingUp, ShieldCheck, Award, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function Localhero() {
  const shouldReduceMotion = useReducedMotion();
  const trustPoints = ["Work directly with Yassine", "Tailored Bolton SEO Strategy", "Transparent Monthly Reporting", "No Agency Middlemen"];
  const heroStats = [{
    label: "Client Organic Growth",
    value: "+185%"
  }, {
    label: "Direct Consultant Access",
    value: "100%"
  }, {
    label: "Local & UK Rank Wins",
    value: "500+"
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localherobolton" data-nav="dark" className="relative w-full bg-primary text-primary-foreground py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.05)_0%,transparent_70%)]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <motion.div className="lg:col-span-7 flex flex-col justify-center text-left" variants={containerVariants} initial="hidden" animate="visible">
            <motion.div variants={itemVariants} className="mb-6 flex flex-wrap items-center gap-2">
              <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary/90 px-3 py-1 text-sm font-medium rounded-full flex items-center gap-1.5 shadow-sm">
                <MapPin className="size-4 text-accent" />
                <span>Bolton &amp; Greater Manchester</span>
              </Badge>
              <Badge variant="outline" className="border-primary-foreground/20 text-primary-foreground/90 px-3 py-1 text-sm font-normal rounded-full flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-accent" />
                <span>Independent Freelance Specialist</span>
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.15]">
              Local SEO Consultant in Bolton
            </motion.h1>

            <motion.p variants={itemVariants} className="mt-6 text-base sm:text-lg md:text-xl text-primary-foreground/85 font-sans max-w-2xl leading-relaxed">
              Hi, I&apos;m Yassine Essaadi. I help Bolton and Greater Manchester businesses win high-intent Google searches and outperform local competitors. You work directly with me—no account managers, no generic agency templates, just transparent expertise focused on your revenue.
            </motion.p>

            <motion.div variants={itemVariants} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              {trustPoints.map((point, index) => <div key={index} data-index={index} className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-5 text-accent shrink-0" />
                  <span className="text-sm md:text-base text-primary-foreground/90 font-medium">
                    {point}
                  </span>
                </div>)}
            </motion.div>

            <motion.div variants={itemVariants} className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-medium text-base h-12 px-6 rounded-lg shadow-md transition-all hover:translate-y-[-1px]">
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-5 mr-2" /></SafeHydrate>
                  Book a Free SEO Consultation
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 font-medium text-base h-12 px-6 rounded-lg transition-all">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-10 pt-6 border-t border-primary-foreground/15 flex items-center gap-6">
              <div className="flex items-center gap-2 text-xs md:text-sm text-primary-foreground/80">
                <ShieldCheck className="size-4 text-accent shrink-0" />
                <span>Verified Client Case Studies</span>
              </div>
              <div className="flex items-center gap-2 text-xs md:text-sm text-primary-foreground/80">
                <Award className="size-4 text-accent shrink-0" />
                <span>Ethical &amp; Sustainable Growth</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div className="lg:col-span-5 relative flex justify-center items-center" initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
            <div className="relative w-full max-w-md">
              <div className="absolute inset-0 bg-accent/20 rounded-2xl transform rotate-3 scale-105 blur-sm" />

              <div className="relative rounded-2xl overflow-hidden border-2 border-primary-foreground/20 bg-primary/80 shadow-2xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant in Bolton" loading="eager" className="w-full h-auto aspect-4/5 object-cover object-top" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-transparent to-transparent" />

                <div className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur-md p-4 rounded-xl text-foreground shadow-lg border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-serif text-base font-bold text-foreground">Yassine Essaadi</p>
                      <p className="text-xs text-muted-foreground font-sans">Lead SEO Strategy Consultant</p>
                    </div>
                    <Badge variant="secondary" className="bg-secondary text-secondary-foreground text-xs font-semibold px-2.5 py-1">
                      Direct Access
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="absolute -top-6 -right-6 hidden sm:flex items-center gap-3 bg-background text-foreground p-3.5 rounded-xl shadow-xl border border-border">
                <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground">
                  <TrendingUp className="size-5 text-accent" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Avg. Organic Lift</p>
                  <p className="text-lg font-bold font-serif text-foreground leading-tight">+185%</p>
                </div>
              </div>

              <div className="absolute -bottom-6 -left-6 hidden sm:flex items-center gap-3 bg-background text-foreground py-2.5 px-4 rounded-xl shadow-xl border border-border">
                <MapPin className="size-4 text-primary shrink-0" />
                <span className="text-xs font-semibold text-foreground">Bolton &amp; UK Search Growth</span>
              </div>

            </div>
          </motion.div>

        </div>

        <div className="mt-16 sm:mt-20 pt-8 border-t border-primary-foreground/15 grid grid-cols-1 md:grid-cols-3 gap-6">
          {heroStats.map((stat, i) => <div key={i} data-index={i} className="flex items-center justify-between sm:justify-start sm:gap-4 p-4 rounded-xl bg-primary-foreground/5 border border-primary-foreground/10">
              <span className="text-2xl sm:text-3xl font-bold font-serif text-accent">{stat.value}</span>
              <span className="text-xs sm:text-sm text-primary-foreground/80 font-sans font-medium">{stat.label}</span>
            </div>)}
        </div>

      </div>
    </section>;
}